import { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Alert, Form } from "react-bootstrap";
import { Building, CreditCard } from "react-bootstrap-icons";
import { Select } from "../../Controls/Select";
import { createApiService } from "../../shared/apiConfig";
import { 
    BillingApi, 
    FacilityApi,
    ServerRequestsBillingPlanDto
} from "../../shared/api/axios-client";

interface FacilityInfo {
    facilityId: number;
    name: string;
    currentPlans: Array<{
        billingPlanId: number;
        billingPlanName: string;
    }>;
}

interface FacilityAssignmentModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    operatorId: number;
    billingPlans: ServerRequestsBillingPlanDto[];
    selectedFacilities: number[];
    onSaved: () => void;
    singleFacilityMode?: boolean; // When true, only show the selected facility
    preSelectedPlanId?: number | null; // When provided, pre-select this plan
}

export function FacilityAssignmentModal({ 
    show, 
    setShow, 
    operatorId, 
    billingPlans, 
    selectedFacilities, 
    onSaved,
    singleFacilityMode = false,
    preSelectedPlanId = null
}: FacilityAssignmentModalProps) {
    const [facilities, setFacilities] = useState<FacilityInfo[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [facilitiesToAssign, setFacilitiesToAssign] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
    // Save/Cancel flow only; no separate remove confirmation

    const billingService = createApiService(BillingApi);
    const facilityService = createApiService(FacilityApi);

    useEffect(() => {
        if (show) {
            fetchFacilities();
            setSelectedPlanId(preSelectedPlanId);
            setFacilitiesToAssign(selectedFacilities);
            setError("");
            
        }
    }, [show, operatorId, selectedFacilities, preSelectedPlanId]);

    // Auto-select facilities that have the selected billing plan
    useEffect(() => {
        if (selectedPlanId && facilities.length > 0) {
            const facilitiesWithThisPlan = facilities
                .filter(f => f.currentPlans.some(plan => plan.billingPlanId === selectedPlanId))
                .map(f => f.facilityId);
            
            // If we're in single facility mode, keep the originally selected facilities
            // Otherwise, auto-select facilities that have this plan
            if (singleFacilityMode) {
                // Keep the selected facilities as they are
            } else {
                setFacilitiesToAssign(facilitiesWithThisPlan);
            }
        }
    }, [selectedPlanId, facilities, singleFacilityMode]);

    const fetchFacilities = async () => {
        setIsLoadingFacilities(true);
        try {
            // Get operator facilities and their billing plan assignments in parallel
            const [facilitiesResponse, billingPlansResponse] = await Promise.all([
                facilityService.listFacilitiesLite({
                    operatorId: operatorId
                }),
                billingService.getOperatorFacilityBillingPlans({
                    operatorId: operatorId
                })
            ]);

            const facilityBillingPlans = billingPlansResponse.data?.billingPlans || [];
            
            // Create a map of facility ID to array of billing plan assignments
            const facilityPlansMap = new Map<number, typeof facilityBillingPlans>();
            facilityBillingPlans.forEach(plan => {
                if (plan.facilityId) {
                    const existing = facilityPlansMap.get(plan.facilityId) || [];
                    facilityPlansMap.set(plan.facilityId, [...existing, plan]);
                }
            });

            // Transform facilities data with billing plan assignments
            let allFacilities = (facilitiesResponse.data?.items || []).map(facility => {
                const facilityPlans = facilityPlansMap.get(facility.facilityId!) || [];
                
                return {
                    facilityId: facility.facilityId!,
                    name: facility.name || `Facility ${facility.facilityId}`,
                    currentPlans: facilityPlans.map(plan => ({
                        billingPlanId: plan.billingPlanId!,
                        billingPlanName: plan.billingPlanName || `Plan ${plan.billingPlanId}`
                    }))
                };
            });

            // If in single facility mode, only show the selected facility
            if (singleFacilityMode && selectedFacilities.length > 0) {
                allFacilities = allFacilities.filter(f => 
                    selectedFacilities.includes(f.facilityId)
                );
            }

            setFacilities(allFacilities);
        } catch (error) {
            console.error("Error loading facilities:", error);
            setError("Failed to load facilities");
        } finally {
            setIsLoadingFacilities(false);
        }
    };

    const handleFacilityToggle = (facilityId: number) => {
        setFacilitiesToAssign(prev => 
            prev.includes(facilityId) 
                ? prev.filter(id => id !== facilityId)
                : [...prev, facilityId]
        );
    };

    const handleSelectAll = () => {
        setFacilitiesToAssign(facilities.map(f => f.facilityId));
    };

    const handleSelectNone = () => {
        setFacilitiesToAssign([]);
    };

    const handleSaveChanges = async () => {
        setIsLoading(true);
        setError("");

        try {
            if (!selectedPlanId) {
                setError("Please select a billing plan");
                return;
            }
            // Determine current assignments for the selected plan
            const currentlyAssigned = facilities
                .filter(f => f.currentPlans.some(p => p.billingPlanId === selectedPlanId))
                .map(f => f.facilityId);

            const currentlyAssignedSet = new Set(currentlyAssigned);
            const nextAssignedSet = new Set(facilitiesToAssign);

            // Compute additions and removals based on checkbox state
            const toAdd: number[] = facilitiesToAssign.filter(id => !currentlyAssignedSet.has(id));
            const toRemove: number[] = currentlyAssigned.filter(id => !nextAssignedSet.has(id));

            // If nothing changed, just close
            if (toAdd.length === 0 && toRemove.length === 0) {
                setShow(false);
                return;
            }

            // Execute API calls for adds/removes
            const ops: Promise<any>[] = [];
            if (toAdd.length > 0) {
                ops.push(billingService.assignBillingPlanToFacilities({
                    planId: selectedPlanId,
                    requestBody: toAdd
                }));
            }
            if (toRemove.length > 0) {
                ops.push(billingService.removeBillingPlanFromFacilities({
                    planId: selectedPlanId,
                    requestBody: toRemove
                }));
            }

            await Promise.all(ops);
            onSaved();
            setShow(false);
        } catch (error: any) {
            console.error("Error saving billing plan assignments:", error);
            setError(error.response?.data?.message || "Failed to save changes");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleSingleFacilityAction = async () => {
        setIsLoading(true);
        setError("");

        try {
            if (!selectedPlanId) {
                setError("Please select a billing plan");
                return;
            }

            const targetFacilityIds = facilities.map(f => f.facilityId);
            if (targetFacilityIds.length === 0) {
                setShow(false);
                return;
            }

            const hasSelectedPlan = facilities.some(f =>
                f.currentPlans.some(p => p.billingPlanId === selectedPlanId)
            );

            if (hasSelectedPlan) {
                await billingService.removeBillingPlanFromFacilities({
                    planId: selectedPlanId,
                    requestBody: targetFacilityIds
                });
            } else {
                await billingService.assignBillingPlanToFacilities({
                    planId: selectedPlanId,
                    requestBody: targetFacilityIds
                });
            }

            onSaved();
            setShow(false);
        } catch (error: any) {
            console.error("Error updating single facility plan:", error);
            setError(error.response?.data?.message || "Failed to update plan");
        } finally {
            setIsLoading(false);
        }
    };

    const selectedFacilitiesWithPlans = facilitiesToAssign
        .map(id => facilities.find(f => f.facilityId === id))
        .filter(f => f && f.currentPlans.length > 0);
    
    const selectedFacilitiesWithSelectedPlan = facilitiesToAssign
        .map(id => facilities.find(f => f.facilityId === id))
        .filter(f => f && f.currentPlans.some(plan => plan.billingPlanId === selectedPlanId));

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {singleFacilityMode ? "Change Facility Billing Plan" : "Assign Billing Plans to Facilities"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                {isLoadingFacilities ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading facilities...</span>
                        </div>
                        <div className="mt-2">Loading facilities...</div>
                    </div>
                ) : (
                    <>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Select
                                    label="Select Billing Plan"
                                    value={selectedPlanId || ""}
                                    onChange={(e) => setSelectedPlanId(e.target.value ? parseInt(e.target.value) : null)}
                                    required
                                >
                                    <option value="">Choose a billing plan...</option>
                                    {billingPlans.map(plan => (
                                        <option key={plan.billingPlanId} value={plan.billingPlanId}>
                                            {plan.name || `Plan ${plan.billingPlanId}`}
                                        </option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>

                        {selectedPlanId ? (
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    {!singleFacilityMode && (
                                        <h6 className="mb-0">Select Facilities ({facilitiesToAssign.length} selected)</h6>
                                    )}
                                    {!singleFacilityMode && (
                                        <div className="btn-group" role="group">
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm" 
                                                onClick={handleSelectAll}
                                            >
                                                Select All
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm" 
                                                onClick={handleSelectNone}
                                            >
                                                Select None
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                                    {facilities.length === 0 ? (
                                        <div className="text-muted text-center py-3">
                                            No facilities found for this operator
                                        </div>
                                    ) : (
                                        facilities.map(facility => (
                                            <div key={facility.facilityId} className="form-check mb-2">
                                                {singleFacilityMode ? (
                                                    <div className="d-flex align-items-center ps-1">
                                                        <Building className="me-2" size={16} />
                                                        <div>
                                                            <div className="fw-medium">{facility.name}</div>
                                                            {facility.currentPlans.length > 0 ? (
                                                                <div className="text-muted small">
                                                                    <CreditCard className="me-1" size={12} />
                                                                    Currently: {facility.currentPlans.length === 1 
                                                                        ? facility.currentPlans[0].billingPlanName
                                                                        : `${facility.currentPlans.length} plans (${facility.currentPlans.map(p => p.billingPlanName).join(', ')})`}
                                                                </div>
                                                            ) : (
                                                                <div className="text-muted small">No plans assigned</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`facility-${facility.facilityId}`}
                                                        checked={facilitiesToAssign.includes(facility.facilityId)}
                                                        onChange={() => handleFacilityToggle(facility.facilityId)}
                                                        label={
                                                            <div className="d-flex align-items-center">
                                                                <Building className="me-2" size={16} />
                                                                <div>
                                                                    <div className="fw-medium">{facility.name}</div>
                                                                    {facility.currentPlans.length > 0 ? (
                                                                        <div className="text-muted small">
                                                                            <CreditCard className="me-1" size={12} />
                                                                            Currently: {facility.currentPlans.length === 1 
                                                                                ? facility.currentPlans[0].billingPlanName
                                                                                : `${facility.currentPlans.length} plans (${facility.currentPlans.map(p => p.billingPlanName).join(', ')})`}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-muted small">No plans assigned</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        }
                                                    />
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <CreditCard size={48} className="mb-3" />
                                    <h6>Select a billing plan above</h6>
                                </div>
                            </div>
                        )}

                        {selectedPlanId && !singleFacilityMode && (
                            <Alert variant="info">
                                <strong>Tip:</strong> Checked means the plan will be assigned; unchecked means it will be removed for that facility when you save.
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                {singleFacilityMode ? (
                    (() => {
                        const hasSelectedPlan = !!selectedPlanId && facilities.some(f =>
                            f.currentPlans.some(p => p.billingPlanId === selectedPlanId)
                        );
                        return (
                            <Button
                                variant={hasSelectedPlan ? "danger" : "primary"}
                                onClick={handleSingleFacilityAction}
                                disabled={isLoading || !selectedPlanId}
                            >
                                {isLoading ? (hasSelectedPlan ? "Removing..." : "Adding...") : (hasSelectedPlan ? "Remove Plan" : "Add Plan")}
                            </Button>
                        );
                    })()
                ) : (
                    <Button 
                        variant="primary" 
                        onClick={handleSaveChanges} 
                        disabled={isLoading || !selectedPlanId}
                    >
                        {isLoading ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}